/**
 * Full-size viewer for the lot gallery: photographs (the `full` variant) or
 * floor plans (the stored original). Rendered through a portal over the page,
 * closed by Escape, the backdrop or the close button; arrow keys and the two
 * buttons move through the set. Styles: `.lightbox*` in theme/features/property.css.
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react'
import { photoVariant } from '../../api/format'
import type { MediaRecord } from '../../api/types'
import { useOverlay } from '../../ui/overlays'

export type ViewerKind = 'photos' | 'plans'
export interface ViewerState { kind: ViewerKind; index: number }

export function Lightbox({ viewer, items, address, onChange, onClose }: {
  viewer: ViewerState | null
  items: MediaRecord[]
  address: string
  onChange: (index: number) => void
  onClose: () => void
}) {
  const open = viewer !== null && items.length > 0
  useOverlay(open, onClose)
  const length = items.length
  const index = viewer ? ((viewer.index % Math.max(1, length)) + length) % Math.max(1, length) : 0
  const move = (direction: -1 | 1) => { if (length > 1) onChange((index + direction + length) % length) }

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1) }
      else if (event.key === 'ArrowRight') { event.preventDefault(); move(1) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  // Preload the neighbours so moving through the set does not flash.
  useEffect(() => {
    if (!open || length < 2) return
    const urls = [items[(index + 1) % length], items[(index - 1 + length) % length]].map(m => m ? src(m, viewer!.kind) : null)
    const images = urls.filter((u): u is string => Boolean(u)).map(u => { const image = new Image(); image.src = u; return image })
    return () => { for (const image of images) if (!image.complete) image.src = '' }
  }, [open, index, length, items, viewer])

  if (!open || !viewer) return null
  const current = items[index]
  if (!current) return null
  const title = viewer.kind === 'plans' ? 'Floor plan' : 'Property photos'
  return createPortal(
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={title} onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
      <div className="lightbox-head">
        <span className="lightbox-title">{title}</span>
        {length > 1 ? <span className="lightbox-count">{index + 1} / {length}</span> : null}
        <span className="sp" />
        <button type="button" className="lightbox-close" onClick={onClose} aria-label="Close"><IconX className="ic-lg" /></button>
      </div>
      <img className="lightbox-img" src={src(current, viewer.kind)} alt={current.caption?.trim() || `${address} ${viewer.kind === 'plans' ? 'floor plan' : 'photo'}`} decoding="async" referrerPolicy="no-referrer" draggable={false} />
      {length > 1 ? <>
        <button type="button" className="lightbox-nav prev" onClick={() => move(-1)} aria-label="Previous full-size image"><IconChevronLeft className="ic-lg" /></button>
        <button type="button" className="lightbox-nav next" onClick={() => move(1)} aria-label="Next full-size image"><IconChevronRight className="ic-lg" /></button>
      </> : null}
    </div>,
    document.body,
  )
}

function src(media: MediaRecord, kind: ViewerKind): string {
  // Floor plans are served as stored; the photo pipeline has sized variants.
  return kind === 'photos' ? photoVariant(media.url, 'full') : media.url
}
